import { ViewportPortal } from '@xyflow/react';
import { useLayoutEffect, useRef } from 'react';
import type { CanvasAwarenessState } from './awareness-types';
import styles from './remote-cursors.module.css';

/**
 * Filled-arrow cursor silhouette in a 16×24 box. Pointing up-and-left so the
 * tip sits at (0, 0) — matches macOS/Windows native cursor pose.
 */
const CURSOR_ARROW_PATH = 'M0 0 L0 16 L4 12 L7 18 L9 17 L6 11 L11 11 Z';

export interface RemoteCursorsProps {
  remoteStates: Map<number, CanvasAwarenessState>;
}

/**
 * Renders remote peer cursors in flow-space via React Flow's ViewportPortal.
 *
 * Why ViewportPortal: cursor coordinates from `useCursorAwareness.reportPointer`
 * are in flow-space (pre-pan, pre-zoom). The portal renders its children
 * inside the React Flow viewport DIV that RF transforms on pan/zoom, so the
 * coordinates stay in flow-space and RF applies the transform automatically.
 *
 * Cursor positions are written imperatively (transform) at the raw awareness
 * cadence — no perfect-cursors smoothing. Smoothing was tried but the spline
 * trails fast-moving cursors over multiple frames, producing a visible lag
 * that felt worse than the 30Hz raw cadence. (Edge ghosts in
 * `RemoteInFlightEdges` keep smoothing; the spline-trailing problem is
 * less perceptible when the moving end is constrained to a path.)
 */
export function RemoteCursors({ remoteStates }: RemoteCursorsProps) {
  const visible: Array<{ id: number; state: CanvasAwarenessState }> = [];
  remoteStates.forEach((state, id) => {
    if (!state.canvasCursor) return;
    visible.push({ id, state });
  });

  if (visible.length === 0) return null;

  return (
    <ViewportPortal>
      {visible.map(({ id, state }) => (
        <RemoteCursor
          key={id}
          clientId={id}
          x={state.canvasCursor!.x}
          y={state.canvasCursor!.y}
          color={state.user.color}
          name={state.user.name}
        />
      ))}
    </ViewportPortal>
  );
}

interface RemoteCursorProps {
  clientId: number;
  x: number;
  y: number;
  color: string;
  name: string;
}

function RemoteCursor({ clientId, x, y, color, name }: RemoteCursorProps) {
  const ref = useRef<SVGSVGElement>(null);

  // Raw imperative position write. No spline interpolation — fast cursor
  // sweeps would otherwise trail behind because perfect-cursors needs
  // multiple frames of input to produce smooth output.
  useLayoutEffect(() => {
    const elm = ref.current;
    if (!elm) return;
    elm.style.setProperty('transform', `translate(${x}px, ${y}px)`);
  }, [x, y]);

  return (
    <svg
      ref={ref}
      data-testid="awareness-cursor"
      data-user-id={String(clientId)}
      className={styles.cursor}
      width={16}
      height={24}
      fill={color}
    >
      <path d={CURSOR_ARROW_PATH} />
      <text x={14} y={20} fill={color} className={styles.label}>
        {name}
      </text>
    </svg>
  );
}
